import React, { useEffect, useMemo, useState } from 'react';
import { getFacilityDisplayName } from '../data/sponsors';

const MESSAGE_PLACEHOLDER =
  'Please be specific on what you are looking for and what the dates are.';

type InquiryType = 'information' | 'scheduling';

const DEFAULT_SERVICES = [
  'Weddings',
  'Parties',
  'Reunions',
  'Sporting Events',
  'Practices',
  'Fitness Area',
  getFacilityDisplayName('walking-track'),
  'Other'
];

interface ContactUsButtonProps {
  buttonText: React.ReactNode;
  className?: string;
  defaultService?: string;
  defaultInquiry?: InquiryType;
}

const ContactUsButton: React.FC<ContactUsButtonProps> = ({
  buttonText,
  className,
  defaultService,
  defaultInquiry = 'information'
}) => {
  const serviceOptions = useMemo(() => {
    if (!defaultService) {
      return DEFAULT_SERVICES;
    }
    return DEFAULT_SERVICES.includes(defaultService)
      ? DEFAULT_SERVICES
      : [defaultService, ...DEFAULT_SERVICES];
  }, [defaultService]);

  const [isOpen, setIsOpen] = useState(false);
  const [service, setService] = useState(defaultService || serviceOptions[0]);
  const [inquiryType, setInquiryType] = useState<InquiryType>(defaultInquiry);
  const [fullName, setFullName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [errors, setErrors] = useState<{ email?: string; message?: string }>({});

  const handleOpen = () => {
    setIsOpen(true);
    setStatus('idle');
    setStatusMessage('');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (status === 'sending') {
      return;
    }
    const nextErrors: { email?: string; message?: string } = {};

    if (!replyTo.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(replyTo.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!message.trim()) {
      nextErrors.message = 'Please add a brief message.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('error');
      setStatusMessage('Please fix the highlighted fields.');
      return;
    }
    setErrors({});

    const subject = `${service} - ${
      inquiryType === 'scheduling' ? 'Scheduling Request' : 'Information Request'
    }`;
    const body = message.trim() || MESSAGE_PLACEHOLDER;

    setStatus('sending');
    setStatusMessage('Sending message...');

    fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject,
        service,
        inquiryType: inquiryType === 'scheduling' ? 'Schedule' : 'Information',
        replyTo,
        fullName,
        message: body
      })
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const errorMessage = errorBody?.error || 'Email failed to send.';
          throw new Error(errorMessage);
        }
        setStatus('success');
        setStatusMessage('Email sent successfully. We will be in touch soon!');
        setMessage('');
        setReplyTo('');
        setFullName('');
        setErrors({});
        setTimeout(() => {
          handleClose();
        }, 5000);
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setStatus('error');
        setStatusMessage(error.message || 'Email failed to send. Please try again in a moment.');
      });
  };

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleOpen();
        }}
        className={className}
      >
        {buttonText}
      </button>

      {isOpen && (
        <div className="modal-overlay">
          <div
            className="modal"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <img
              src="/ec_logo_white.png"
              alt="TDS Event Center"
              className="contact-modal-logo"
            />
            {status === 'success' ? (
              <div className="contact-success">
                <div className="contact-success-icon">✓</div>
                <h3>Message Sent</h3>
                <p>Thanks for reaching out. We will be in touch shortly.</p>
                <p className="contact-success-note">Closing in a few seconds…</p>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  Close now
                </button>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h3>Contact Us</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={handleClose}
                    aria-label="Close contact form"
                    disabled={status === 'sending'}
                  >
                    ×
                  </button>
                </div>
                <p>
                  Select the service you need and whether you are looking for information or ready to
                  schedule.
                </p>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label htmlFor="contactService">Service</label>
                    <select
                      id="contactService"
                      value={service}
                      onChange={(event) => setService(event.target.value)}
                      disabled={status === 'sending'}
                    >
                      {serviceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactInquiry">I am looking to</label>
                    <select
                      id="contactInquiry"
                      value={inquiryType}
                      onChange={(event) => setInquiryType(event.target.value as InquiryType)}
                      disabled={status === 'sending'}
                    >
                      <option value="information">Get information</option>
                      <option value="scheduling">Schedule an event</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactName">Your name</label>
                    <input
                      id="contactName"
                      type="text"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      placeholder="Your full name"
                      disabled={status === 'sending'}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactEmail">Your email</label>
                    <input
                      id="contactEmail"
                      type="email"
                      value={replyTo}
                      onChange={(event) => {
                        setReplyTo(event.target.value);
                        if (errors.email) {
                          setErrors((prev) => ({ ...prev, email: undefined }));
                        }
                      }}
                      placeholder="you@example.com"
                      disabled={status === 'sending'}
                    />
                    {errors.email && <div className="form-error">{errors.email}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactMessage">Details</label>
                    <textarea
                      id="contactMessage"
                      value={message}
                      onChange={(event) => {
                        setMessage(event.target.value);
                        if (errors.message) {
                          setErrors((prev) => ({ ...prev, message: undefined }));
                        }
                      }}
                      placeholder={MESSAGE_PLACEHOLDER}
                      rows={6}
                      disabled={status === 'sending'}
                    />
                    {errors.message && <div className="form-error">{errors.message}</div>}
                  </div>
                  {status !== 'idle' && (
                    <div className={`contact-status contact-status-${status}`} role="status">
                      {statusMessage}
                    </div>
                  )}
                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn btn-secondary"
                      disabled={status === 'sending'}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={status === 'sending'}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleSubmit();
                      }}
                    >
                      {status === 'sending' ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ContactUsButton;
